import "@/App.css";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Menu, X, ShoppingBag, ShieldCheck, Truck, ChevronDown, Phone, Mail, MessageCircle, Plus } from "lucide-react";
import { toast, Toaster } from "sonner";
import { CartModal, OrdersModal, PRODUCTS } from "@/components/shop";
import { AuthModal, ProfileModal, AuthCallback, ResetPassword } from "@/components/auth";
import { AdminDashboard } from "@/components/AdminDashboard";
import { FeedbackSection } from "@/components/FeedbackSection";
import { Reveal, MaskedLine, Marquee, BotanicalFloat } from "@/components/motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "+91 7208656816";
const PHONE_TEL = "tel:+917208656816";
const WHATSAPP_URL = "https://wa.me/917208656816?text=Hi%20Jamvanya!%20I%20would%20like%20to%20order%20your%20moringa%20powder.";
const EMAIL = "jamvanya25@gmail.com";
const productImage = "/products/hero-flatlay.png";
const farmImage = "https://images.unsplash.com/photo-1681569127368-5d0a60757e0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const WholesaleModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", quantity: "", message: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try { await axios.post(`${API}/enquiries`, { ...form, enquiry_type: "wholesale" }); toast.success("Enquiry received", { description: "We’ll be in touch shortly." }); onClose(); }
    catch { toast.error("Something went wrong", { description: "Please try again in a moment." }); }
    finally { setSending(false); }
  };
  return (
    <div className="modal-backdrop" data-testid="enquiry-modal">
      <div className="modal-card">
        <button className="close-btn" onClick={onClose} data-testid="close-enquiry-button"><X size={20}/></button>
        <span className="eyebrow">Partner with us</span>
        <h2>Grow with Jamvanya.</h2>
        <p className="modal-intro">Tell us about your needs and we’ll create a wholesale plan for you.</p>
        <form onSubmit={submit} data-testid="wholesale-enquiry-form">
          <input required placeholder="Your name" value={form.name} onChange={e => setForm({...form,name:e.target.value})} data-testid="enquiry-name-input"/>
          <input required type="email" placeholder="Email address" value={form.email} onChange={e => setForm({...form,email:e.target.value})} data-testid="enquiry-email-input"/>
          <input placeholder="Phone number (optional)" value={form.phone} onChange={e => setForm({...form,phone:e.target.value})} data-testid="enquiry-phone-input"/>
          <input placeholder="Estimated monthly volume" value={form.quantity} onChange={e => setForm({...form,quantity:e.target.value})} data-testid="enquiry-quantity-input"/>
          <textarea placeholder="Anything else we should know?" value={form.message} onChange={e => setForm({...form,message:e.target.value})} data-testid="enquiry-message-input"/>
          <button className="button button-primary full" disabled={sending} data-testid="submit-enquiry-button">{sending ? "Sending…" : "Send enquiry"}<ArrowRight size={16}/></button>
        </form>
      </div>
    </div>
  );
};

const Home = () => {
  const [modal, setModal] = useState(null);
  const [menu, setMenu] = useState(false);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jamvanya_cart") || "{}"); }
    catch { return {}; }
  });
  const [user, setUser] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const oauthPending = window.location.hash?.includes("session_id=");
  const resetToken = window.location.hash?.includes("reset-token=") ? window.location.hash.split("reset-token=")[1]?.split("&")[0] : null;

  useEffect(() => {
    try { localStorage.setItem("jamvanya_cart", JSON.stringify(cart)); } catch {}
  }, [cart]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let frame;
    const raf = (t) => { lenis.raf(t); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); lenis.destroy(); };
  }, []);

  useEffect(() => {
    // CRITICAL: skip the /me check when returning from Google OAuth — AuthCallback exchanges the session_id first
    if (window.location.hash?.includes("session_id=")) { setUser(false); return; }
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(data);
      } catch {
        try {
          const { data } = await axios.post(`${API}/auth/refresh`, {});
          if (!cancelled) setUser(data);
        } catch { if (!cancelled) setUser(false); }
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const [stock, setStock] = useState({});
  useEffect(() => {
    axios.get(`${API}/stock`).then(r => setStock(r.data)).catch(() => {});
  }, []);
  const cartCount = Object.values(cart).reduce((s, n) => s + n, 0);
  const addToCart = (id) => {
    const p = PRODUCTS.find(x => x.id === id);
    if (stock[id] === false) {
      toast.error("Sold out", { description: `${p.name} is currently unavailable.` });
      return;
    }
    setCart(c => ({ ...c, [id]: Math.min(20, (c[id] || 0) + 1) }));
    toast.success("Added to cart", { description: `${p.name} ${p.size} · ₹${p.price}` });
  };
  const openOrders = () => {
    if (user) { setModal("orders"); }
    else { toast.message("Log in to view your orders"); setModal("auth"); }
    setMenu(false);
  };

  if (oauthPending && !user) {
    return <AuthCallback onDone={(u) => setUser(u || false)} />;
  }
  if (resetToken) {
    return (
      <main>
        <Toaster position="top-right"/>
        <ResetPassword token={resetToken} onDone={(success) => {
          window.history.replaceState(null, "", window.location.pathname);
          if (success) setModal("auth");
        }}/>
      </main>
    );
  }
  return (
    <main>
      <Toaster position="top-right"/>
      <div className="announcement" data-testid="announcement-bar">Special offer: 20% off on orders above ₹999</div>
      <nav className="nav shell">
        <a href="#top" className="brand" data-testid="brand-logo"><img className="brand-logo" src="/logo.png" alt="Jamvanya logo" data-testid="brand-logo-image"/><span>Jamvanya</span><small className="brand-tagline" data-testid="brand-tagline">Organic processed foods</small></a>
        <div className={`nav-links ${menu ? "open" : ""}`}>
          <a href="#why" onClick={()=>setMenu(false)} data-testid="nav-why-link">Why dehydrated</a>
          <a href="#story" onClick={()=>setMenu(false)} data-testid="nav-story-link">Our story</a>
          <a href="#shop" onClick={()=>setMenu(false)} data-testid="nav-shop-link">Shop</a>
          <button className="nav-orders-link" onClick={openOrders} data-testid="nav-orders-button">My orders</button>
          {user ? (
            <button className="nav-user-chip" onClick={()=>setModal("profile")} data-testid="nav-profile-button"><span className="user-initial">{(user.name || user.email || "J").charAt(0).toUpperCase()}</span> {user.name ? user.name.split(" ")[0] : "Account"}</button>
          ) : (
            <button className="nav-orders-link" onClick={()=>setModal("auth")} data-testid="nav-login-button">Login</button>
          )}
          <a href={PHONE_TEL} className="nav-phone" data-testid="nav-phone-link"><Phone size={14}/> {PHONE_DISPLAY}</a>
          <button className="nav-wholesale" onClick={()=>setModal("wholesale")} data-testid="nav-wholesale-button">Wholesale <ArrowRight size={15}/></button>
        </div>
        <div className="nav-actions">
          <button className="menu-button" onClick={()=>setMenu(!menu)} data-testid="mobile-menu-button">{menu ? <X/> : <Menu/>}</button>
        </div>
      </nav>
      <section className="hero shell" id="top" ref={heroRef}>
        <div className="hero-copy">
          <motion.span className="eyebrow" data-testid="hero-eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}><span className="dot"/> Dehydrated farm staples · Jamkhed</motion.span>
          <h1 data-testid="hero-heading"><MaskedLine delay={0.15}>From our farms.</MaskedLine><MaskedLine delay={0.32}><em>To your kitchen.</em></MaskedLine></h1>
          <motion.p className="hero-text" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}>Moringa, mango, pineapple, shiitake and more — grown on family farms in Jamkhed, gently dehydrated and milled to keep every bit of flavour and nourishment intact.</motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}>
            <a className="button button-primary" href="#shop" data-testid="hero-order-button">Shop the pantry <ArrowRight size={17}/></a>
            <a className="text-link" href="#why" data-testid="hero-learn-link">Why dehydrated <ChevronDown size={16}/></a>
          </motion.div>
          <motion.div className="hero-proof" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.9 }}><div className="proof-icon"><Check size={15}/></div><span><strong>100% single-ingredient</strong><br/>Nothing added. Nothing hidden.</span></motion.div>
        </div>
        <motion.div className="hero-visual" style={{ y: heroImgY }} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}>
          <div className="image-frame"><img src={productImage} alt="Sun-dried dehydrated staples from Jamvanya farms" data-testid="hero-product-image"/></div>
        </motion.div>
      </section>
      <Marquee items={["100% single-ingredient", "No preservatives, ever", "Hygienically processed", "Quality-checked batches", "Straight from our farms", "Secure online payments", "Trusted by families", "Jamkhed · Maharashtra"]}/>
      <section className="benefits shell" id="why">
        <BotanicalFloat className="leaf-a" variant={0} speed={95} rotate={16}/>
        <BotanicalFloat className="leaf-b" variant={1} speed={60} rotate={10}/>
        <BotanicalFloat className="leaf-c" variant={2} speed={120} rotate={20}/>
        <Reveal className="section-heading split">
          <div>
            <span className="eyebrow">Why dehydrated</span>
            <h2>Freshness,<br/><em>locked in.</em></h2>
          </div>
          <p>Dehydration is our whole philosophy: pick at peak ripeness, remove only the water, and seal in everything else. No preservatives, no cold chain, no compromise — just the harvest, made to last.</p>
        </Reveal>
        <Reveal className="process-strip" delay={0.08}>
          <div className="process-step" data-testid="process-step-harvest"><span>01</span><b>Harvested at peak</b><small>Leaves, bulbs and fruits picked at their freshest on family farms in Jamkhed</small></div>
          <div className="process-step" data-testid="process-step-dry"><span>02</span><b>Gently dehydrated</b><small>Low, slow drying removes only water — never the flavour or nourishment</small></div>
          <div className="process-step" data-testid="process-step-mill"><span>03</span><b>Milled in small batches</b><small>Milled and sealed within days of drying, so nothing sits in storage</small></div>
          <div className="process-step" data-testid="process-step-pantry"><span>04</span><b>Pantry-ready for months</b><small>No fridge, no additives — a pure single ingredient, always in season</small></div>
        </Reveal>
      </section>
      <section className="shop-section" id="shop">
        <div className="shell">
          <Reveal className="shop-head">
            <span className="eyebrow">The pantry</span>
            <h2>Dehydrated delights,<br/><em>from our farms.</em></h2>
            <p>Eight small-batch staples, gently dehydrated and milled in Jamkhed. Every pouch is 250g of pure, single-ingredient goodness.</p>
          </Reveal>
          <div className="catalog-stack" data-testid="product-catalog">
            {PRODUCTS.map((p, idx) => (
              <motion.article key={p.id} className="stack-card" data-testid={`product-tile-${p.id}`}
                initial={{ opacity: 0, scale: 0.92 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: 0.06 * (idx % 4), ease: [0.22, 1, 0.36, 1] }}>
                <div className="stack-img-wrap">
                  {stock[p.id] === false && <span className="sold-out-badge" data-testid={`sold-out-${p.id}`}>Sold out</span>}
                  <img className="stack-img" src={p.image} alt={p.name} data-testid={`product-image-${p.id}`}/>
                </div>
                <div className="stack-body">
                  <div className="tile-top"><span className="tile-number">{String(idx + 1).padStart(2, "0")}</span><span className="tile-dot" style={{ background: p.color }}/></div>
                  <h3>{p.name}</h3>
                  <p>{p.note}</p>
                  <div className="tile-bottom">
                    <b data-testid={`product-price-${p.id}`}>{p.size} · ₹{p.price}</b>
                    <button disabled={stock[p.id] === false} onClick={() => addToCart(p.id)} data-testid={`add-to-cart-${p.id}`}>{stock[p.id] === false ? "Sold out" : "Add to cart"} <Plus size={13}/></button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
          <p className="shop-disclaimer" data-testid="image-disclaimer">Disclaimer: product images shown are for representation only and are not the actual packaging. The actual product may differ from the images shown.</p>
        </div>
      </section>
      <section className="story shell" id="story">
        <Reveal className="story-image"><img src={farmImage} alt="Moringa leaves growing on a sunny farm in Jamkhed" data-testid="farm-story-image"/><span className="story-caption">Where every leaf begins<br/><b>Jamkhed · Ahilyanagar, Maharashtra</b></span></Reveal>
        <Reveal className="story-copy" delay={0.12}>
          <span className="eyebrow">A better kind of supply chain</span>
          <h2>Good for you.<br/><em>Good for the ground.</em></h2>
          <p>At Jamvanya Private Limited, we bridge the gap between conscientious farming and high-grade food processing. Headquartered in Maharashtra, our operations integrate sustainable, farm-gate agricultural practices with controlled processing to deliver consistent, nutrient-dense products—including premium dehydrated produce.</p>
          <p>By maintaining hands-on oversight across cultivation, hygienic drying, and rigorous quality inspection, we guarantee complete supply chain traceability, food-grade safety standards, and reliable volume fulfillment. Whether supplying domestic distributors or international trade partners, Jamvanya is built on transparent relationships, strict batch-to-batch consistency, and a steadfast commitment to delivering pure, unadulterated agricultural goods directly from the source.</p>
          <div className="cert-strip" data-testid="certifications-strip">
            <span className="cert-badge" data-testid="cert-fssai"><ShieldCheck size={14}/> FSSAI Certified</span>
            <span className="cert-badge" data-testid="cert-natural"><Check size={14}/> 100% Single-Ingredient</span>
          </div>
          <p className="signature"><b>With gratitude,</b><br/><span>Jamvanya</span></p>
          <a className="text-link" href="#contact" data-testid="story-contact-link">Get to know us <ArrowRight size={16}/></a>
        </Reveal>
      </section>
      <section className="wholesale" id="contact">
        <div className="shell wholesale-panel">
          <Reveal>
            <span className="eyebrow">Bulk & wholesale</span>
            <h2>Buying in bulk?<br/><em>Let's talk.</em></h2>
            <p className="wholesale-intro">Hotels, cafes, retailers and food makers — we mill and pack bulk orders to your specification, straight from our farms in Jamkhed.</p>
            <span className="price-note" data-testid="wholesale-price-note">For prices, contact us — we'll build a quote around your volumes.</span>
            <button className="button button-light" onClick={()=>setModal("wholesale")} data-testid="wholesale-enquiry-button">Start a wholesale conversation <ArrowRight size={17}/></button>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="contact-cards">
              <a className="contact-card" href={PHONE_TEL} data-testid="contact-phone-link"><Phone size={17}/><span><b>Call us</b><small>{PHONE_DISPLAY}</small></span><ArrowRight size={15}/></a>
              <a className="contact-card" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link"><MessageCircle size={17}/><span><b>WhatsApp us</b><small>{PHONE_DISPLAY}</small></span><ArrowRight size={15}/></a>
              <a className="contact-card" href={`mailto:${EMAIL}`} data-testid="contact-email-link"><Mail size={17}/><span><b>Write to us</b><small>{EMAIL}</small></span><ArrowRight size={15}/></a>
            </div>
          </Reveal>
        </div>
      </section>
      <FeedbackSection user={user}/>
      <footer>
        <div className="shell footer-main">
          <img className="footer-logo" src="/logo.png" alt="Jamvanya emblem" data-testid="footer-logo"/>
          <a href="#top" className="brand" data-testid="footer-brand-link"><span>Jamvanya <span>Private Limited</span></span></a>
          <p>Pure nourishment, rooted in care.</p>
          <div className="footer-links">
            <a href="#why" data-testid="footer-why-link">Why dehydrated</a>
            <a href="#story" data-testid="footer-story-link">Our story</a>
            <a href="#contact" data-testid="footer-contact-link">Wholesale</a>
            <a href={PHONE_TEL} data-testid="footer-phone-link">{PHONE_DISPLAY}</a>
            <a href={`mailto:${EMAIL}`} data-testid="footer-email-link">{EMAIL}</a>
            <a href="/admin" data-testid="footer-admin-link">Admin login</a>
          </div>
        </div>
        <div className="shell footer-legal" data-testid="footer-legal">
          <span><b>CIN</b> U10790PN2026PTC255488</span>
          <span><b>GSTIN</b> 27AAHCJ4115FIZS</span>
          <span><b>Registered address</b> 5981/4610 Tapneshwar Road, Jamkhed, Ahilyanagar – 413201, Maharashtra</span>
          <span><b>Contact officer</b> Himanshu Madhav Kusekar · <a href="mailto:kusekarhimanshu897@gmail.com" data-testid="footer-officer-email">kusekarhimanshu897@gmail.com</a></span>
        </div>
        <div className="shell footer-bottom"><span>© 2026 Jamvanya Private Limited</span><span>Made with care for a greener everyday.</span></div>
      </footer>
      <button className="cart-float" onClick={()=>setModal("cart")} aria-label="Open cart" data-testid="nav-cart-button"><ShoppingBag size={22}/>{cartCount > 0 && <span className="cart-badge" data-testid="cart-count-badge">{cartCount}</span>}</button>
      <a className="whatsapp-float" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="Chat with Jamvanya on WhatsApp" data-testid="whatsapp-chat-button"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413"/></svg></a>
      {modal === "cart" && <CartModal cart={cart} setCart={setCart} user={user} onClose={()=>setModal(null)}/>}
      {modal === "orders" && user && <OrdersModal user={user} onClose={()=>setModal(null)}/>}
      {modal === "auth" && <AuthModal onClose={()=>setModal(null)} onAuth={(u)=>setUser(u)}/>}
      {modal === "profile" && user && <ProfileModal user={user} onClose={()=>setModal(null)} onLogout={()=>setUser(false)}/>}
      {modal === "wholesale" && <WholesaleModal onClose={()=>setModal(null)}/>}
    </main>
  );
};

function App() {
  if (window.location.pathname === "/admin") return <AdminDashboard />;
  return <Home />;
}

export default App;
